import UserComment from '@/store/models/user/UserComment'
import CollectionItem from '@/store/models/collectionItems/CollectionItem'

// Shared Vuex ORM mutations for threaded comments, used by both the
// collectionItem (private) and collections (public) store modules.

// Comments + their embedded latest replies as one insertable list —
// Vuex ORM drops unknown nested fields, so latest replies must be inserted explicitly
export function withLatestReplies(comments) {
  return [...comments, ...comments.map(comment => comment.latestReply).filter(Boolean)]
}

export async function adjustParentRepliesCount(parentId, delta) {
  if (!parentId) return
  const parent = UserComment.find(parentId)
  if (!parent) return

  await UserComment.update({
    where: parentId,
    data: { repliesCount: Math.max(0, parent.repliesCount + delta) },
  })
}

// Mirror the server-side thread cascade locally; returns the removed record
// (callers use its parentId/repliesCount for follow-up count adjustments)
export async function removeCommentThreadLocally(commentId) {
  const comment = UserComment.find(commentId)
  await UserComment.delete(commentId)
  await UserComment.delete(record => record.parentId === commentId)
  await adjustParentRepliesCount(comment?.parentId, -1)
  return comment
}

export async function applyCommentLikeState(comment, liked) {
  await UserComment.update({
    where: comment.id,
    data: {
      isLikedByUser: liked,
      likesCount: Math.max(0, comment.likesCount + (liked ? 1 : -1)),
    },
  })
}

// Mirror a hide/unhide locally: the comment's flag, its author's block state on every comment of
// theirs on the item (blocks are the author's, not one comment's), and the item's count, which covers
// the comments everyone may see (like the server's): a hidden top-level comment takes its thread out
// with it, a reply inside a hidden thread is out already.
export async function applyCommentModeration(commentId, { hidden, authorBlocked, canUnblock }, itemId) {
  const comment = UserComment.find(commentId)
  if (!comment) return

  await UserComment.update({
    where: record => record.resourceId === itemId && record.userId === comment.userId,
    data: { authorBlocked, canUnblock },
  })
  await UserComment.update({ where: commentId, data: { hidden } })
  if (comment.hidden === hidden) return

  const threadHidden = Boolean(comment.parentId && UserComment.find(comment.parentId)?.hidden)
  const item = CollectionItem.find(itemId)
  if (!item || threadHidden) return

  const moved = 1 + (comment.parentId ? 0 : comment.repliesCount)
  await CollectionItem.update({
    where: itemId,
    data: { commentsCount: Math.max(0, (item.commentsCount || 0) + (hidden ? -moved : moved)) },
  })
}
